/**
 * PassingMarkWarning.js
 * Reusable component that displays a warning when the passing mark exceeds total marks
 * for quizzes with "Actual Score" setting.
 *
 * Props:
 * - scoreType: The scoring type ("Actual Score" or "Percentage")
 * - passingScore: The required passing score/mark
 * - questions: Array of questions with mark property
 * - mb: Optional margin bottom (defaults to 4)
 */

import PropTypes from 'prop-types';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
} from '@chakra-ui/react';

/**
 * Calculates the total marks from an array of questions
 * @param {Array} questions - Array of question objects with mark property
 * @returns {number} Total marks
 */
const calculateTotalMarks = (questions) => {
  return questions.reduce((sum, question) => sum + question.mark, 0);
};

/**
 * Determines if warning should be shown based on quiz configuration
 * @param {string} scoreType - The scoring type
 * @param {number} passingScore - The passing score/mark
 * @param {Array} questions - Array of questions
 * @returns {boolean} Whether to show warning
 */
const shouldShowWarning = (scoreType, passingScore, questions) => {
  return (
    scoreType === 'Actual Score' &&
    questions.length > 0 &&
    passingScore > calculateTotalMarks(questions)
  );
};

function PassingMarkWarning({ scoreType, passingScore, questions, mb = 4 }) {
  // Don't render anything if warning shouldn't be shown
  if (!shouldShowWarning(scoreType, passingScore, questions)) {
    return null;
  }

  const totalMarks = calculateTotalMarks(questions);

  return (
    <Alert status="warning" mb={mb}>
      <AlertIcon />
      <Box>
        <AlertTitle>Passing Mark Warning!</AlertTitle>
        <AlertDescription>
          The passing mark ({passingScore}) exceeds the total available marks (
          {totalMarks}). Please adjust the passing mark in the Quiz tab or
          increase the total marks of your questions.
        </AlertDescription>
      </Box>
    </Alert>
  );
}

PassingMarkWarning.propTypes = {
  scoreType: PropTypes.string.isRequired,
  passingScore: PropTypes.number.isRequired,
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      mark: PropTypes.number.isRequired,
    })
  ).isRequired,
  mb: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

PassingMarkWarning.defaultProps = {
  mb: 4,
};

export default PassingMarkWarning;
